import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { getBaseTerms } from '../api/terms';
import { getPlanTerms, updatePlanTerms } from '../api/plan';
import './TermsEdit.css';
import { usePlans } from '../utils/usePlans';

const TermsEdit = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { oneTimeOptions, subscriptionOptions } = usePlans();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      plan: null,
      general_content: '',
      coupon_content: '',
      is_template: false,
    },
  });

  const selectedPlan = watch('plan');
  const planId = selectedPlan?.id ?? null;

  const SELECT_OPTIONS = useMemo(
    () => [
      ...oneTimeOptions.map((p) => ({ id: p.id, label: p.name })),
      ...subscriptionOptions.map((p) => ({ id: p.id, label: p.name })),
    ],
    [oneTimeOptions, subscriptionOptions],
  );

  useEffect(() => {
    if (!planId) {
      reset({
        plan: null,
        general_content: '',
        coupon_content: '',
        is_template: false,
      });
      setIsLoading(false);
      return;
    }

    const planOption = SELECT_OPTIONS.find((p) => p.id === planId) ?? {
      id: planId,
      label: `プラン (ID: ${planId})`,
    };

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const response = await getPlanTerms(planId);
        if (cancelled) {
          return;
        }
        const { general, coupon } = response;
        reset({
          plan: planOption,
          general_content: general ?? '',
          coupon_content: coupon ?? '',
          is_template: false,
        });
      } catch (error) {
        if (!cancelled) {
          console.error('利用規約の読み込みに失敗しました:', error);
          setAlert({
            open: true,
            message: error.message || '利用規約の読み込みに失敗しました',
            severity: 'error',
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [planId, SELECT_OPTIONS, reset]);

  const onSubmit = async (values) => {
    if (!values.plan?.id) return;
    if (
      String(values.general_content ?? '').trim() === '' ||
      String(values.coupon_content ?? '').trim() === ''
    ) {
      setAlert({
        open: true,
        message: '利用規約とクーポン利用規約の両方の内容を入力してください',
        severity: 'warning',
      });
      return;
    }
    setIsSaving(true);
    try {
      const termsData = {
        general_content: values.general_content,
        coupon_content: values.coupon_content,
        is_template: Boolean(values.is_template),
      };
      const response = await updatePlanTerms(values.plan.id, termsData);

      reset({
        ...values,
        is_template: Boolean(false),
      });
      setAlert({
        open: true,
        message: response.message || '利用規約とクーポン利用規約を保存しました',
        severity: 'success',
      });
    } catch (error) {
      console.error('保存に失敗しました:', error);
      setAlert({
        open: true,
        message: error.message || '保存に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  /** ベース規約（テンプレート）を取得してフォームに反映 */
  const handleApplyTemplate = async () => {
    if (!selectedPlan?.id) {
      setAlert({
        open: true,
        message: '先にプランを選択してください',
        severity: 'warning',
      });
      return;
    }
    setIsApplyingTemplate(true);
    try {
      const base = await getBaseTerms();
      if (!base.success) {
        throw new Error(base.message || 'ベース規約の取得に失敗しました');
      }
      const { general, coupon } = base;

      setValue('general_content', general, { shouldDirty: true });
      setValue('coupon_content', coupon, { shouldDirty: true });
      setValue('is_template', true, { shouldDirty: true });
      setAlert({
        open: true,
        message: 'ベース規約（テンプレート）をフォームに反映しました',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      setAlert({
        open: true,
        message: error.message || 'テンプレートの取得に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  const planSelectRowSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    mb: 2,
    flexWrap: 'wrap',
  };

  if (!planId) {
    return (
      <Container className="terms-edit-container" maxWidth="lg">
        <Paper className="terms-edit-paper" elevation={3}>
          <Box className="terms-edit-header">
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ backgroundColor: 'transparent' }}
            >
              プラン別利用規約編集
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/terms-management')}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              規約管理に戻る
            </Button>
          </Box>
          <Box className="terms-editor">
            <Box sx={planSelectRowSx}>
              <Controller
                name="plan"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={SELECT_OPTIONS}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    getOptionLabel={(option) => option.label}
                    getOptionKey={(option) => option.id}
                    renderInput={(params) => (
                      <TextField {...params} label="プラン" />
                    )}
                    sx={{ flex: '1 1 280px', minWidth: 200, maxWidth: 480 }}
                    size="small"
                  />
                )}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleApplyTemplate}
                disabled={!selectedPlan?.id || isApplyingTemplate}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
                title={
                  selectedPlan
                    ? 'ベース規約を取得して入力欄に反映します'
                    : 'プラン選択後に利用できます'
                }
              >
                {isApplyingTemplate ? '取得中...' : 'テンプレート適用'}
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              規約を編集するプランを選択してください
            </Alert>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container className="terms-edit-container" maxWidth="lg">
      <Paper className="terms-edit-paper" elevation={3}>
        <Box className="terms-edit-header">
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ backgroundColor: 'transparent' }}
          >
            プラン別利用規約編集
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/terms-management')}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            規約管理に戻る
          </Button>
        </Box>

        {isDirty && (
          <Alert severity="info" sx={{ mb: 2 }}>
            未保存の変更があります
          </Alert>
        )}

        <Box className="terms-editor">
          <Box sx={planSelectRowSx}>
            <Controller
              name="plan"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={SELECT_OPTIONS}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  getOptionLabel={(option) => option.label}
                  getOptionKey={(option) => option.id}
                  renderInput={(params) => (
                    <TextField {...params} label="プラン" />
                  )}
                  sx={{ flex: '1 1 280px', minWidth: 200, maxWidth: 480 }}
                  size="small"
                />
              )}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={handleApplyTemplate}
              disabled={!selectedPlan?.id || isApplyingTemplate || isLoading}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                alignSelf: 'center',
                flexShrink: 0,
              }}
              title="ベース規約を取得して入力欄に反映します"
            >
              {isApplyingTemplate ? '取得中...' : 'テンプレート適用'}
            </Button>
          </Box>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="240px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box className="terms-section">
                <Typography variant="h6" gutterBottom>
                  利用規約内容
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  rows={5}
                  {...register('general_content')}
                  placeholder="利用規約の内容を入力してください..."
                  variant="outlined"
                  className="terms-textfield"
                />
              </Box>

              <Box className="coupon-terms-section">
                <Typography variant="h6" gutterBottom>
                  クーポン利用規約内容
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  rows={5}
                  {...register('coupon_content')}
                  placeholder="クーポン利用規約の内容を入力してください..."
                  variant="outlined"
                  className="terms-textfield coupon-terms"
                />
              </Box>
            </>
          )}
        </Box>

        <Box className="save-button-container">
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isSaving || !isDirty}
            className="save-button green"
            size="large"
            sx={{
              backgroundColor: '#4caf50',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
              },
              minWidth: '200px',
              padding: '12px 24px',
            }}
          >
            {isSaving ? '保存中...' : '保存'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TermsEdit;
